<template>
  <div
    id="flashcard-view-container-v2"
    ref="flashcard-view-container"
    class="flashcard-view-container"
  >
    <breadcrumbs
      :breadcrumbs="breadcrumbs"
      version="v2"
    ></breadcrumbs>
    <template
      v-if="answered"
    >
      <answered-flashcard
        :answer-ids="answerIds"
        :category="currentFlashcardCategory"
        :correct="correct"
        :flashcard="currentFlashcard"
        :raw-answer-data="rawAnswerData"
      ></answered-flashcard>
      <answered-footer
        :show-flashcard-statuses="!isScenarioCategoryRoute"
        @tag="tryTagFlashcard"
      ></answered-footer>
    </template>

    <template
      v-else
    >
      <unanswered-header
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
      ></unanswered-header>
      <unanswered-flashcard
        :answer-ids="answerIds"
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
        :raw-answer-data="rawAnswerData"
        :sharing-enabled="sharingEnabled"
        :terminology="isTerminology"
        @set-answer-ids="setAnswerIds"
        @set-raw-answer-data="setRawAnswerData"
        @try-answer-flashcard="tryAnswerFlashcard"
      ></unanswered-flashcard>
      <unanswered-footer
        :can-submit-answer="canSubmitAnswer"
        :category="currentFlashcardCategory"
        :flashcard="currentFlashcard"
        @answer="answerFlashcard"
        @next-flashcard="goToNextFlashcard"
        @previous-flashcard="goToPreviousFlashcard"
      ></unanswered-footer>
    </template>
  </div>
</template>

<script>
import AnsweredFlashcard from '@/components/flashcards-v2/AnsweredFlashcard';
import AnsweredFooter from '@components/flashcards-v2/AnsweredFooter';
import Breadcrumbs from '@components/singletons/breadcrumbs';
import UnansweredFlashcard from '@components/flashcards-v2/UnansweredFlashcard';
import UnansweredFooter from '@components/flashcards-v2/UnansweredFooter';
import UnansweredHeader from '@/components/flashcards-v2/UnansweredHeader.vue';
import FlashcardViewMixin from '@/mixins/flashcardViews';
import GlobalMixin from '@/mixins/global';
import RateLimitMixin from '@/mixins/rate-limit';
import QuestionsUIV2Mixin from '@/mixins/questions-ui-v2';
import QuestionOfTheDayMixin from '@/mixins/qotd';
import { isScenarioReviewRoute } from '@/composables/useScenarioBox';
import { numSelectionsSatisfied } from '@/utils/flashcards';

export default {
  components: {
    AnsweredFlashcard,
    AnsweredFooter,
    Breadcrumbs,
    UnansweredFlashcard,
    UnansweredFooter,
    UnansweredHeader,
  },
  mixins: [
    FlashcardViewMixin,
    GlobalMixin,
    RateLimitMixin,
    QuestionsUIV2Mixin,
    QuestionOfTheDayMixin,
  ],
  // this is designed to capture users who have used the back button after starting the tour
  // so that we can reset the onTour variable and re-enable parts of the app we blur/disable during the tour
  beforeRouteLeave(to, from, next) {
    if (this.$store.getters['session/onTour']) {
      // the component at categories#complete will redirect to categories#index when a user is on the tour
      // so we need to make sure we don't reset the onTour variable when the user goes to that route
      if (to.name !== 'flashcard#show' && to.name !== 'categories#complete') {
        this.$store.commit('session/setOnTour', false);
        this.$store.commit('session/setTourType', '');
        this.$store.commit('navigationItems/resetDynamicNavigationItems');
      }
    }
    next();
  },
  data() {
    return {
      scenarioReviewMode: false,
    };
  },
  computed: {
    isScenarioCategoryRoute() {
      return isScenarioReviewRoute(this.$route);
    },
    breadcrumbs() {
      if (!this.currentFlashcardCategory) return [];
      let categories = this.currentFlashcardCategory.path.split('.').map(id => this.$store.getters['categories/categories'][id]);
      if (this.scenarioReviewMode) {
        const category = this.$store.getters['categories/categories'][this.$route.params.categoryId];
        return [
          { name: `${category?.name || ''} Results`.trim(), route: {name: 'scenarios#results', params: { categoryId: this.$route.params.categoryId }, query: this.currentQuery}},
          { name: 'Review' },
        ];
      }
      if (this.isScenarioCategoryRoute) {
        return categories.map(c => {
          return { name: c.name, route: {name: 'scenarios#category', params: { categoryTypeId: c.categoryTypeId, categoryId: c.id}, query: this.currentQuery}};
        });
      }
      return categories.map(c => {
        return { name: c.name, route: {name: 'categories#index', params: { categoryId: c.id}, query: this.currentQuery}};
      });
    },
    canSubmitAnswer() {
      return this.hasBeenAnswered && numSelectionsSatisfied(this.currentFlashcard, this.answerIds);
    },
    categoryTypeId() {
      return this.$route.params.categoryTypeId || null;
    },
    categoryType() {
      if (this.categoryTypeId) {
        return this.$store.getters['categoryTypes/all'][this.categoryTypeId];
      }
      return null;
    },
    currentFlashcardCategory() {
      if (this.currentFlashcard?.id) {
        let categoryId = this.$route.params.categoryId;

        if (this.categoryTypeId) {
          const categories = this.currentFlashcard
            .categoryIds.map(id => this.$store.getters['categories/categories'][id])
            .filter(c => c)
            .filter(c => c.categoryTypeId == this.categoryTypeId);
          return categories[0];
        }

        const categories = this.currentFlashcard.categoryIds.map(id => this.$store.getters['categories/categories'][id]).filter(c => c);

        if (categoryId) {
          if (categoryId === 'all') {
            return categories.find(c => c.categoryTypeId == this.currentResource.resourceId);
          } else {
            let category = categories.find(c => c.id == categoryId);

            if (!category) {
              // this happens if we are studying from a top level category and flashcard belongs to child catgory
              let parent = this.$store.getters['categories/categories'][categoryId];
              let retVal;

              parent.descendants.forEach(id => {
                if (this.currentFlashcard.categoryIds.includes(id)) {
                  retVal = this.$store.getters['categories/categories'][id];
                  return;
                }
              });

              return retVal;
            } else {
              return category;
            }
          }
        }
      }
    },
    // Used in flashcardViews mixin
    nextFlashcardRoute() {
      if (!this.nextFlashcard) {
        if (this.isScenarioCategoryRoute) {
          return { name: 'scenarios#results', params: { categoryId: this.$route.params.categoryId }, query: this.currentQuery };
        }
        if (this.categoryTypeId) {
          return { name: 'categoryTypes#complete', params: { categoryTypeId: this.categoryTypeId }, query: this.currentQuery };
        }

        return { name: 'categories#complete', params: { categoryId: this.$route.params.categoryId }, query: this.currentQuery };
      }

      if (this.isScenarioCategoryRoute) {
        return {
          name: 'scenarios#review',
          params: {
            categoryId: this.$route.params.categoryId,
            flashcardId: this.nextFlashcard,
          },
          query: this.currentQuery,
        };
      }

      return {
        name: this.getCorrectRouteName(this.nextFlashcard.type),
        params: {
          categoryId: this.$route.params.categoryId,
          flashcardId: this.nextFlashcard,
        },
        query: this.currentQuery,
      };
    },
    previousFlashcardRoute() {
      if (this.isScenarioCategoryRoute) {
        return {
          name: 'scenarios#review',
          params: {
            categoryId: this.$route.params.categoryId,
            flashcardId: this.previousFlashcard,
          },
          query: this.currentQuery,
        };
      }

      return {
        name: this.getCorrectRouteName(this.previousFlashcard.type),
        params: {
          categoryId: this.$route.params.categoryId,
          flashcardId: this.previousFlashcard,
        },
        query: this.currentQuery,
      };
    },
    onTour() {
      return this.$store.getters['session/onTour'];
    },
    sharingEnabled() {
      const enabled = !!(this.$store.getters['productVars/get']['flashcardShareMessage']);
      return enabled && !this.onTour;
    },
    shouldShowRateLimitModal() {
      if (this.isQotD) {
        return false;
      }

      if (!this.flashcardIsRateLimited(this.currentFlashcard, this.currentFlashcardCategory)) {
        return false;
      }

      if (!this.creditLimitReached) {
        return false;
      }

      return true;
    },
  },
  watch: {
    '$route.params.flashcardId': function(id) {
      if (this.$store.getters['session/onTour']) {
        this.$analytics.events.viewedOnboardingFlashcard(id);
      }
      if(!this.$store.getters['flashcards/flashcards'][id]) {
        let params = { ids: [id], rateLimited: this.rateLimitingEnabled };
        this.$store.dispatch('flashcards/fetchFlashcards', params);
      }
    },
    'currentFlashcard.id': {
      immediate: true,
      handler: function(id) {
        if (this.isScenarioCategoryRoute && this.$route.params.flashcardId) {
          // Review mode: restore answer state from the store
          this.resetFlashcardFromState(id);
          // resetFlashcardFromState sets answered=false; for review we need answered=true
          if (this.correct !== null) {
            this.answered = true;
          }
        } else {
          this.resetFlashcard(id);
        }
      },
    },
  },
  created() {
    let stateParams = ['bookmarks', 'flashcards', 'stats'];
    this.$store.dispatch('flashcardStates/fetchStates', stateParams);

    // When reviewing a specific flashcard on a scenario route (e.g. from the results page),
    // skip session creation — the category may be complete and the session would return empty.
    // Instead, ensure the flashcard is loaded and let the component render directly.
    if (this.isScenarioCategoryRoute && this.$route.params.flashcardId) {
      this.scenarioReviewMode = true;

      if (!this.currentFlashcard.id) {
        this.$store.dispatch('flashcards/fetchFlashcards', { ids: [this.$route.params.flashcardId] });
      }

      // Build a review session from loaded flashcards so next/previous navigation works
      const categoryId = parseInt(this.$route.params.categoryId);
      const reviewIds = this.$store.getters['flashcards/flashcardList']
        .filter(f => f.categoryIds && f.categoryIds.includes(categoryId))
        .map(f => f.id);

      // Sort by scenario box grouping order if available
      const grouping = this.$store.getters['groupings/list'].find(g => {
        return g?.groupingType === 'Scenario Box'
          && g?.productAssociations.find(pa => pa.categoryId === categoryId);
      });
      if (grouping) {
        const flashcardOrder = grouping.flashcardAssociations.map(aa => aa.assetBId);
        reviewIds.sort((a, b) => {
          const indexA = flashcardOrder.indexOf(a);
          const indexB = flashcardOrder.indexOf(b);
          return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
        });
      }

      if (reviewIds.length) {
        this.$store.commit('flashcards/setFlashcardSession', reviewIds);
      }

      this.sessionPromise = null;
      return;
    }

    let params = {};

    if (this.categoryTypeId) {
      params.categoryTypeId = this.categoryTypeId;
    } else if (this.$route.params.categoryId === 'all') {
      params.categoryTypeId = this.currentResource.resourceId;
    } else if (this.$route.params.categoryId !== 'all') {
      params.categoryId = this.$route.params.categoryId;
    }

    if (this.$route.params.flashcardId) {
      params.flashcardId = this.$route.params.flashcardId;
    }

    const filter = this.$store.getters['flashcardStatuses/flashcardStatusFilter'];
    if (filter.name !== 'unanswered') {
      if (filter.name === 'bookmarked') {
        params.flashcardStatusId = 'bookmarked';
      } else {
        params.flashcardStatusId = filter.id;
      }
    }

    params.rateLimited = this.rateLimitingEnabled;

    if (this.isQotD) {
      this.sessionPromise = this.$store.dispatch('flashcards/fetchQuestionOfTheDay');
    } else {
      this.sessionPromise = this.$store.dispatch('flashcards/createFlashcardSession', params);
    }
  },
  mounted() {
    if (!this.sessionPromise) {
      return;
    }

    this.sessionPromise.then(data => {
      let flashcard, categoryId;
      if (this.isQotD) {
        flashcard = data.flashcard;
        categoryId = data.categoryId;
      } else {
        flashcard = data;
        categoryId = this.$route.params.categoryId;
      }

      if (flashcard) {
        const routeName = this.isScenarioCategoryRoute
          ? 'scenarios#review'
          : this.getCorrectRouteName(flashcard.type);

        this.$router.replace({
          name: routeName,
          params: { categoryId, flashcardId: flashcard.id},
          query: this.currentQuery,
        });

        this.$nextTick().then(() => {
          if (this.shouldShowRateLimitModal) {
            this.$modal.show('rate-limit-reached', {
              category: this.currentFlashcardCategory,
              flashcard: this.currentFlashcard,
            });
          }
        });
      } else {
        // When reviewing an answered flashcard on a scenario route, the session returns empty
        // because the category is complete. Don't redirect — the flashcard is already loaded.
        if (this.isScenarioCategoryRoute && this.$route.params.flashcardId) {
          return;
        }
        const fallbackRoute = this.isScenarioCategoryRoute ? 'scenarios#category' : 'categories#index';
        this.$router.push({ name: fallbackRoute, params: { categoryId: this.$route.params.categoryId }, query: this.currentQuery });
      }
    }).catch(e => {
      if (this.isScenarioCategoryRoute && this.$route.params.flashcardId) {
        return;
      }
      this.$router.go(-1);
      Promise.reject(e);
    });
  },
  methods: {
    tryAnswerFlashcard() {
      if (this.canSubmitAnswer) {
        this.answerFlashcard();
      }
    },
    tryTagFlashcard(statusId) {
      if (this.isScenarioCategoryRoute) {
        this.goToNextFlashcard();
        this.scrollToTop();
        return;
      }
      if (this.isQotD) {
        this.tagWithModal(statusId);
      } else {
        this.tagFlashcard(statusId);
      }
    },
  },
};
</script>

<style lang='scss' scoped>
  #flashcard-view-container-v2 {
    @include flashcard-view-container-v2;
  }
</style>
